/* ==========================================================================
   Paolina Charm Studio - cart handlers  (pr-charm-cart.js)
   Loaded globally from theme.liquid. Handles the grouped "design card"
   controls (copies stepper + remove group) rendered in the cart drawer
   and on the cart page.

   The card exposes:
     [data-pcs-group]           = design_id
     [data-pcs-copies]          = current copies (chain line qty)
     [data-pcs-lines]           = JSON [{key, qty}, ...] chain + charm lines
   Controls:
     [data-pcs-copies-minus] [data-pcs-copies-plus] [data-pcs-copies-input]
     [data-pcs-remove-group]
   Dormant until a design exists in the cart.

   Dawn port: three hook points differ from Motion.
     1) cart update route -> window.routes.cart_update_url (Dawn) instead of
        window.theme.routes.cartUpdate (Motion). Keeps market prefixes (/fr/, /de/).
     2) refresh outside the cart page -> the Cart AJAX API returns the re-rendered
        sections in the same response (bundled sections), so one request swaps
        .drawer__inner and the icon bubble. This replaces both the Motion-only
        'cart:build' DOM event and the earlier publish(cartUpdate) port: publishing
        made <cart-drawer-items> re-render ONLY itself and the footer, which left the
        .drawer__inner-empty block and the is-empty class on <cart-drawer> stale --
        emptying the cart from the card's Remove button showed an empty table instead
        of "Your cart is empty". The is-empty class is toggled here for the same reason.
        Replacing .drawer__inner (not #CartDrawer) is exactly what Dawn's
        CartDrawerItems.getSectionsToRender does, so #CartDrawer-Overlay and its click
        listener survive untouched.
     3) 'ajaxProduct:added' bridge: pr-charm-studio.js ends its add-to-cart with that
        DOM event, which in Motion re-populated and opened the drawer. Nothing in Dawn
        listens to it, so the bridge lives here and leans on the native
        CartDrawer.renderContents() -- the same path product-form.js uses.
   ========================================================================== */
(function () {
  'use strict';

  var busy = false;

  function updateCartUrl() {
    return (window.routes && window.routes.cart_update_url) ? window.routes.cart_update_url : '/cart/update.js';
  }
  function cartUrl() {
    return (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
  }
  function ajaxUrl(u) { return /\.js(\?|$)/.test(u) ? u : (u + '.js'); }

  function onCartPage() {
    return document.body && document.body.classList.contains('template-cart');
  }

  function parseSection(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  function renderDrawerInner(html) {
    var target = document.querySelector('#CartDrawer .drawer__inner');
    var source = parseSection(html, '.drawer__inner');
    if (!target || !source) return false;
    target.innerHTML = source.innerHTML;
    return true;
  }

  function renderCartIconBubble(html) {
    var target = document.getElementById('cart-icon-bubble');
    var source = parseSection(html, '.shopify-section');
    if (!target || !source) return false;
    target.innerHTML = source.innerHTML;
    return true;
  }

  function setEmptyState(itemCount) {
    var wrapper = document.querySelector('cart-drawer');
    if (wrapper && typeof itemCount === 'number') wrapper.classList.toggle('is-empty', itemCount === 0);
  }

  function fetchSection(id) {
    return fetch(cartUrl() + '?section_id=' + id, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); });
  }

  /* The rest of the theme learns that the cart changed from the PUB_SUB bus, not
     from the DOM: assets/product-info.js (quantity rules "already in your cart"),
     assets/price-per-item.js and assets/recipient-form.js all subscribe to
     cartUpdate. Rendering the sections by hand skips them, so publish once the DOM
     is in its new state. source 'cart-items' is the one <cart-items> and
     <cart-drawer-items> ignore (assets/cart.js:31-36), so the drawer we have just
     re-rendered does not immediately re-fetch itself.
     Only called where a REAL cartData exists: assets/quick-add-bulk.js:25-27 reads
     event.cartData.items with no guard. */
  function publishCartUpdate(cartData) {
    if (!cartData) return;
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cartData });
  }

  function refresh(cartData) {
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    var sections = (cartData && cartData.sections) || {};
    if (renderDrawerInner(sections['cart-drawer'])) {
      renderCartIconBubble(sections['cart-icon-bubble']);
      setEmptyState(cartData.item_count);
      publishCartUpdate(cartData);
      return;
    }
    // Fallback only: the bundled sections did not come back. Hand the job to Dawn's
    // <cart-drawer-items>, which re-fetches its own section, rather than leave the
    // drawer showing stale lines.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pcs-cart', cartData: cartData });
    }
    fetchSection('cart-icon-bubble')
      .then(renderCartIconBubble)
      .catch(function (err) { console.error('[pcs-cart] cart bubble refresh failed', err); });
    setEmptyState(cartData && cartData.item_count);
  }

  /* Freeze the line lists while a PCS request is in flight, the way Dawn does in
     CartItems.enableLoading (assets/cart.js:257-268). Not cosmetic: the two paths
     address cart lines differently. PCS updates by line KEY (data-pcs-lines),
     while Dawn's cart-remove-button and quantity-input send the POSITIONAL
     data-index to /cart/change.js. Clicking a native Remove while a PCS remove
     (which zeroes 2-3 lines at once) is still in flight sends an index computed on
     the pre-update cart, and if the PCS update lands first that index now points at
     a different line: the wrong product gets removed. The `busy` flag alone only
     guards further PCS actions.
     Both ids are queried, not Dawn's `||`: with cart_type = 'drawer' the drawer is
     rendered on every template (layout/theme.liquid:398), so on /cart BOTH
     #main-cart-items and #CartDrawer-CartItems exist.
     Known limit, identical to Dawn's: pointer-events does not block keyboard
     activation of an already-focused button. Parity is enough here. */
  function setCartBusy(state) {
    // Re-queried at every call on purpose: refresh() replaces .drawer__inner, so a
    // cached #CartDrawer-CartItems would be detached by the time we clear the flag.
    var nodes = document.querySelectorAll('#main-cart-items, #CartDrawer-CartItems');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.toggle('cart__items--disabled', state);
    }
  }

  function postUpdates(updates) {
    if (busy) return;
    busy = true;
    setCartBusy(true);
    fetch(ajaxUrl(updateCartUrl()), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        updates: updates,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      }),
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); }).then(function (cartData) {
      busy = false;
      // Cleared BEFORE refresh() on every outcome, happy path and fallback alike:
      // refresh() may swap .drawer__inner (new, clean nodes) but the fallback branch
      // does not, and a stale .cart__items--disabled there would leave the cart inert.
      setCartBusy(false);
      refresh(cartData);
    }).catch(function (err) {
      busy = false;
      setCartBusy(false);
      console.error('[pcs-cart] update failed', err);
    });
  }

  function card(target) {
    return target.closest ? target.closest('[data-pcs-group]') : null;
  }

  function readLines(cardEl) {
    try { return JSON.parse(cardEl.getAttribute('data-pcs-lines') || '[]'); }
    catch (e) { return []; }
  }

  function scaleCopies(cardEl, newCopies) {
    var copies = parseInt(cardEl.getAttribute('data-pcs-copies'), 10) || 1;
    if (newCopies < 1) newCopies = 1;
    if (newCopies === copies) return;
    var lines = readLines(cardEl);
    var updates = {};
    lines.forEach(function (ln) {
      var perCopy = Math.max(1, Math.round(ln.qty / copies));
      updates[ln.key] = perCopy * newCopies;
    });
    postUpdates(updates);
  }

  function removeGroup(cardEl) {
    var lines = readLines(cardEl);
    var updates = {};
    lines.forEach(function (ln) { updates[ln.key] = 0; });
    if (Object.keys(updates).length) postUpdates(updates);
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    var minus = t.closest ? t.closest('[data-pcs-copies-minus]') : null;
    var plus = t.closest ? t.closest('[data-pcs-copies-plus]') : null;
    var rem = t.closest ? t.closest('[data-pcs-remove-group]') : null;
    if (minus) {
      var c1 = card(minus); if (!c1) return;
      e.preventDefault();
      scaleCopies(c1, (parseInt(c1.getAttribute('data-pcs-copies'), 10) || 1) - 1);
    } else if (plus) {
      var c2 = card(plus); if (!c2) return;
      e.preventDefault();
      scaleCopies(c2, (parseInt(c2.getAttribute('data-pcs-copies'), 10) || 1) + 1);
    } else if (rem) {
      var c3 = card(rem); if (!c3) return;
      e.preventDefault();
      removeGroup(c3);
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target && e.target.matches && e.target.matches('[data-pcs-copies-input]') ? e.target : null;
    if (!input) return;
    var c = card(input); if (!c) return;
    var val = parseInt(input.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    scaleCopies(c, val);
  });

  /* ------------------------------------------------------------------------
     Bridge for 'ajaxProduct:added' (pr-charm-studio.js, end of add-to-cart).
     Motion re-built and opened the drawer on that event; Dawn has no listener.
     One Section Rendering request returns both sections in the shape
     CartDrawer.renderContents() expects, so the drawer is swapped, the overlay
     re-bound and the drawer opened by Dawn's own code.
     ------------------------------------------------------------------------ */
  function openDrawerWithFreshCart() {
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    fetch(cartUrl() + '?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        // is-empty lives on <cart-drawer>, outside every re-rendered section.
        drawer.classList.remove('is-empty');
        if (typeof drawer.renderContents === 'function') {
          drawer.renderContents({ sections: sections });
          return;
        }
        renderDrawerInner(sections['cart-drawer']);
        renderCartIconBubble(sections['cart-icon-bubble']);
        if (typeof drawer.open === 'function') drawer.open();
      })
      .catch(function (err) { console.error('[pcs-cart] drawer refresh after add failed', err); });
  }

  document.addEventListener('ajaxProduct:added', openDrawerWithFreshCart);
})();
